import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export const openCookieSettings = () => {
  window.dispatchEvent(new CustomEvent("open-cookie-settings"));
};

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  preferences: boolean;
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  advertising: false,
  preferences: false,
};

const CookieCategory = ({
  title,
  description,
  checked,
  onChange,
  alwaysActive,
  alwaysActiveLabel,
  ariaLabel,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange?: (checked: boolean) => void;
  alwaysActive?: boolean;
  alwaysActiveLabel?: string;
  ariaLabel?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-secondary/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
          <span className="font-medium text-sm text-foreground">{title}</span>
        </div>
        {alwaysActive ? (
          <span className="text-xs font-medium text-primary px-2 py-1 bg-primary/10 rounded">
            {alwaysActiveLabel}
          </span>
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            <Switch checked={checked} onCheckedChange={onChange} aria-label={ariaLabel} />
          </div>
        )}
      </div>
      {isOpen && (
        <div className="px-4 pb-3 pt-1 border-t border-border">
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        </div>
      )}
    </div>
  );
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(defaultPreferences);
  const { t } = useLanguage();

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setIsVisible(true);
    } else {
      try {
        const saved = JSON.parse(consent);
        setPrefs(saved);
      } catch {
        setIsVisible(true);
      }
    }

    const handleOpenSettings = () => {
      const consent = localStorage.getItem("cookie-consent");
      if (consent) {
        try {
          setPrefs(JSON.parse(consent));
        } catch {
          setPrefs(defaultPreferences);
        }
      }
      setIsVisible(true);
    };
    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  const savePreferences = (preferences: CookiePreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify(preferences));
    setPrefs(preferences);
    setIsVisible(false);

    // Sync with Google Analytics Consent Mode v2
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: preferences.advertising ? 'granted' : 'denied',
        ad_user_data: preferences.advertising ? 'granted' : 'denied',
        ad_personalization: preferences.advertising ? 'granted' : 'denied',
        analytics_storage: preferences.analytics ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    savePreferences({ essential: true, analytics: true, advertising: true, preferences: true });
  };

  const handleDecline = () => {
    savePreferences({ essential: true, analytics: false, advertising: false, preferences: false });
  };

  const handleSave = () => {
    savePreferences({ ...prefs, essential: true });
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-background w-full max-w-2xl mx-4 mb-0 sm:mb-0 rounded-t-2xl sm:rounded-2xl shadow-2xl border border-border max-h-[85vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-bold text-foreground mb-2">🍪 {t("cookie.title")}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-2">
            {t("cookie.text")}
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed mb-5">
            {t("cookie.details")}
          </p>

          <div className="space-y-2 mb-6">
            <CookieCategory
              title={t("cookie.essential.title")}
              description={t("cookie.essential.desc")}
              checked={true}
              alwaysActive
              alwaysActiveLabel={t("cookie.essential.alwaysActive")}
            />
            <CookieCategory
              title={t("cookie.analytics.title")}
              description={t("cookie.analytics.desc")}
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              ariaLabel={t("cookie.analytics.title")}
            />
            <CookieCategory
              title={t("cookie.advertising.title")}
              description={t("cookie.advertising.desc")}
              checked={prefs.advertising}
              onChange={(v) => setPrefs((p) => ({ ...p, advertising: v }))}
              ariaLabel={t("cookie.advertising.title")}
            />
            <CookieCategory
              title={t("cookie.preferences.title")}
              description={t("cookie.preferences.desc")}
              checked={prefs.preferences}
              onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
              ariaLabel={t("cookie.preferences.title")}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={handleDecline} className="flex-1">
                {t("cookie.decline")}
              </Button>
              <Button variant="outline" onClick={handleSave} className="flex-1">
                {t("cookie.save")}
              </Button>
              <Button onClick={handleAcceptAll} className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground">
                {t("cookie.accept")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
